import { useCallback, useMemo } from "react";
import { graphql, readInlineData, useFragment } from "react-relay";
import CreateSeedRandom from "seed-random";

import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { isAfterOrEqual, isBeforeOrEqual } from "shared/lib/utils/dates";

import type { PWAAdvertCampaignAppConfig } from "scmp-app/components/section/default-section/types";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { hooksHomeV2PickMorningStudioArticle$key } from "scmp-app/queries/__generated__/hooksHomeV2PickMorningStudioArticle.graphql";
import type { hooksPremiumNativeAdConfigQuery$key } from "scmp-app/queries/__generated__/hooksPremiumNativeAdConfigQuery.graphql";

export const usePickMorningStudioArticles = (
  seed: string,
  reference?: hooksHomeV2PickMorningStudioArticle$key | null,
) =>
  useMemo(() => {
    const random = CreateSeedRandom(seed);
    const data = readInlineData(
      graphql`
        fragment hooksHomeV2PickMorningStudioArticle on Queue @inline {
          items(first: 9) {
            edges {
              # eslint-disable-next-line relay/unused-fields
              node {
                ...homeNativeArticleContentItemContent
              }
              ...homeRecommendedForYouSponsorArticleQueueItemsEdge
            }
          }
        }
      `,
      reference ?? null,
    );

    const slot1Articles = (data?.items?.edges ?? []).slice(0, 2);
    const slot2Articles = (data?.items?.edges ?? []).slice(2);
    const slot1Picked = slot1Articles.length > 0 ? Math.floor(random() * slot1Articles.length) : 0;
    const slot2Picked = slot2Articles.length > 0 ? Math.floor(random() * slot2Articles.length) : 0;

    /* Slot 3 (Recommended For You sponsored card) draws from the slot-2 pool
       minus the article slot 2 already took, so the two cards never duplicate.
       The extra random() call comes after the slot-1/2 picks, keeping those
       stable for a given seed. */
    const slot3Articles = slot2Articles.filter((_, index) => index !== slot2Picked);
    const slot3Picked = slot3Articles.length > 0 ? Math.floor(random() * slot3Articles.length) : 0;

    return {
      slot1: slot1Articles[slot1Picked] ?? null,
      slot2: slot2Articles[slot2Picked] ?? null,
      slot3: slot3Articles[slot3Picked] ?? null,
    };
  }, [seed, reference]);

export const usePremiumNativeAd = (reference: hooksPremiumNativeAdConfigQuery$key) => {
  const data = useFragment(
    graphql`
      fragment hooksPremiumNativeAdConfigQuery on Query {
        advertConfig: appConfig(filter: { entityId: "scmp_pwa_advert_campaign" }) {
          json
        }
      }
    `,
    reference,
  );

  const advertConfig = (data?.advertConfig?.json ?? {}) as PWAAdvertCampaignAppConfig;
  const homepageConfig = advertConfig?.premiumNativeAd?.homepage;
  const currentTime = new Date();
  const startDateTime = new Date(homepageConfig?.startDateTime ?? 0);
  const endDateTime = new Date(homepageConfig?.endDateTime ?? 0);
  const isEnableByDateTime =
    homepageConfig?.enableByDateTime &&
    isAfterOrEqual(currentTime, startDateTime) &&
    isBeforeOrEqual(currentTime, endDateTime);

  return homepageConfig?.alwaysEnable || isEnableByDateTime;
};

export const useTopStoriesTracking = (articleIds: string[]) => {
  const impressionGA4Event1 = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: articleIds.slice(0, 4).join(","),
        page_type: "homepage",
        widget_name: "top_story1",
      },
      subcategory: "widget",
    }),
    [articleIds],
  );
  const { captureTrackImpressionEventTargetElement: captureTrackImpressionEventElement1 } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event1,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const impressionGA4Event2 = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: articleIds.slice(4, 7).join(","),
        page_type: "homepage",
        widget_name: "top_story5",
      },
      subcategory: "widget",
    }),
    [articleIds],
  );
  const { captureTrackImpressionEventTargetElement: captureTrackImpressionEventElement2 } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event2,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const impressionGA4Event3 = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: articleIds.slice(7, 11).join(","),
        page_type: "homepage",
        widget_name: "top_story8",
      },
      subcategory: "widget",
    }),
    [articleIds],
  );
  const { captureTrackImpressionEventTargetElement: captureTrackImpressionEventElement3 } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event3,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const impressionGA4Event4 = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: articleIds.slice(11, 15).join(","),
        page_type: "homepage",
        widget_name: "top_story12",
      },
      subcategory: "widget",
    }),
    [articleIds],
  );
  const { captureTrackImpressionEventTargetElement: captureTrackImpressionEventElement4 } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event4,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const impressionGA4Event5 = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: articleIds.slice(15).join(","),
        page_type: "homepage",
        widget_name: "top_story16",
      },
      subcategory: "widget",
    }),
    [articleIds],
  );
  const { captureTrackImpressionEventTargetElement: captureTrackImpressionEventElement5 } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event5,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  return {
    captureTrackImpressionEventElement1,
    captureTrackImpressionEventElement2,
    captureTrackImpressionEventElement3,
    captureTrackImpressionEventElement4,
    captureTrackImpressionEventElement5,
  };
};
